/**
 * GameState serialization — single source of truth.
 *
 * 2026-05-26 cleanup: раньше serializer/deserializer + SerializedGameState
 * interface + reviveAtlasCollected дублировались в stores/game.ts и
 * telemetry/cloudSync.ts. Два места = два места править при изменениях
 * shape (особенно опасно для backward compat). Consolidated сюда.
 *
 * Особенности shape:
 *  - `atlasCollected` сериализуется как Array<[phraseId, count]>. Старые
 *    snapshots (Array<string>) принимаются для backward compat: каждый
 *    id → count=1.
 *  - `lessonsUnlocked` остаётся Set<string> ↔ Array<string>.
 *  - `purchasedUpgrades` Map<string, number> ↔ Array<[id, level]>.
 */
import type { GameState } from '@sai-clicker/game-core';

/** JSON-safe shape of GameState — Maps/Sets как Arrays. */
export interface SerializedGameState extends Omit<
  GameState,
  'atlasCollected' | 'lessonsUnlocked' | 'purchasedUpgrades'
> {
  /** New format: tuples [id, count]. Legacy format: bare ids (count=1). */
  readonly atlasCollected: readonly (readonly [string, number])[] | readonly string[];
  readonly lessonsUnlocked: readonly string[];
  readonly purchasedUpgrades: readonly (readonly [string, number])[];
}

export function serializeGameState(s: GameState): SerializedGameState {
  return {
    ...s,
    atlasCollected: Array.from(s.atlasCollected.entries()),
    lessonsUnlocked: Array.from(s.lessonsUnlocked),
    purchasedUpgrades: Array.from(s.purchasedUpgrades.entries()),
  };
}

/**
 * Restore atlasCollected to Map. Two formats supported:
 *  - new format: [[id, count], ...] — direct Map construction.
 *  - legacy format: [id, id, ...] — each id seeded with count=1.
 * Detection: first entry is an array → tuples; first entry is a string → ids.
 */
function reviveAtlasCollected(
  raw: readonly (readonly [string, number])[] | readonly string[],
): Map<string, number> {
  if (raw.length === 0) return new Map();
  const first = raw[0];
  if (Array.isArray(first)) {
    return new Map(raw as readonly (readonly [string, number])[]);
  }
  // Legacy: array of ids (Set persisted). Set has no duplicates by
  // definition, так что .set(id, 1) безопасно overwriting не делает.
  const m = new Map<string, number>();
  for (const id of raw as readonly string[]) {
    m.set(id, 1);
  }
  return m;
}

export function deserializeGameState(s: SerializedGameState): GameState {
  return {
    ...s,
    atlasCollected: reviveAtlasCollected(s.atlasCollected),
    lessonsUnlocked: new Set(s.lessonsUnlocked),
    purchasedUpgrades: new Map(s.purchasedUpgrades),
  };
}
